/* eslint-disable no-console */
import isMobile from "ismobilejs";
import { useEffect, useRef } from "react";

const useSticky = (wrapperId: string, buttonIds: string[], offset: number, top: number) => {
    // Stabilize so inline button id arrays do not rebind the effect every render
    const buttonIdsKey = buttonIds.join("|");
    const stickyOffsetsRef = useRef<number[]>([]);
    const stickyWidthsRef = useRef<number[]>([]);

    useEffect(() => {
        const showMobile = isMobile().any;
        if (showMobile) {
            return undefined;
        }

        const stickyWrappers = document.querySelectorAll(`#${wrapperId}`);
        if (stickyWrappers.length === 0) {
            return undefined;
        }

        const footer = document.getElementsByTagName("footer")[0] as HTMLElement | undefined;
        const resolvedButtonIds = buttonIdsKey ? buttonIdsKey.split("|") : [];

        const buttons = resolvedButtonIds.map((selector: string) => {
            if (selector.startsWith("[")) {
                return document.querySelector(selector) as HTMLElement | null;
            }
            return document.getElementById(selector);
        });

        const keepButtonsInFlow = () => {
            // Prevent Checkout from inheriting position:fixed from the sticky wrapper
            buttons.forEach(button => {
                if (button) {
                    button.style.position = "static";
                    button.style.top = "";
                    button.style.width = "";
                }
            });
        };

        const measure = () => {
            stickyOffsetsRef.current = Array.from(stickyWrappers).map(wrapper => {
                const element = wrapper as HTMLElement;
                return element.getBoundingClientRect().top + window.scrollY;
            });
            stickyWidthsRef.current = Array.from(stickyWrappers).map(wrapper => {
                const element = wrapper as HTMLElement;
                return element.offsetWidth;
            });
        };

        if (
            stickyOffsetsRef.current.length === 0 ||
            stickyWidthsRef.current.some(width => !width || width <= 0)
        ) {
            measure();
        }

        const handleScroll = () => {
            const backToTop = document.getElementById("back-to-top");
            const footerOffset = footer
                ? footer.getBoundingClientRect().top + window.scrollY
                : Number.POSITIVE_INFINITY;
            const backToTopBottom = backToTop
                ? backToTop.getBoundingClientRect().top +
                  window.scrollY +
                  backToTop.getBoundingClientRect().height
                : Number.POSITIVE_INFINITY;

            Array.from(stickyWrappers).forEach((wrapper, index) => {
                const element = wrapper as HTMLElement;
                const stickyHeight = element.offsetHeight;
                const stickyOffset = stickyOffsetsRef.current[index];
                const stickyWidth = stickyWidthsRef.current[index];

                if (stickyOffset == null || !stickyWidth || stickyWidth <= 0) {
                    return;
                }

                const maxStickyTop =
                    Math.min(footerOffset, backToTopBottom) - stickyHeight - offset;

                if (window.scrollY > stickyOffset - offset && window.scrollY < maxStickyTop) {
                    keepButtonsInFlow();
                    element.style.position = "fixed";
                    element.style.top = `${top}px`;
                    element.style.zIndex = "1000";
                    element.style.width = `${stickyWidth}px`;
                    element.style.height = `${stickyHeight}px`;
                } else if (window.scrollY >= maxStickyTop && Number.isFinite(maxStickyTop)) {
                    keepButtonsInFlow();
                    element.style.position = "absolute";
                    element.style.top = `${maxStickyTop - stickyOffset + offset}px`;
                } else {
                    element.style.position = "static";
                    element.style.top = "";
                    element.style.width = "";
                    element.style.height = "";
                    element.style.zIndex = "";
                    keepButtonsInFlow();
                }
            });
        };

        const handleResize = () => {
            Array.from(stickyWrappers).forEach(wrapper => {
                const element = wrapper as HTMLElement;
                element.style.position = "static";
                element.style.top = "";
                element.style.width = "";
                element.style.height = "";
                element.style.zIndex = "";
            });
            measure();
            handleScroll();
        };

        window.addEventListener("scroll", handleScroll);
        window.addEventListener("resize", handleResize);

        handleScroll();

        // Remeasure after cart lines / back-to-top finish rendering
        const retryIds = [300, 1000].map(delay =>
            window.setTimeout(() => {
                handleResize();
            }, delay),
        );

        return () => {
            retryIds.forEach(id => window.clearTimeout(id));
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleResize);
            Array.from(stickyWrappers).forEach(wrapper => {
                const element = wrapper as HTMLElement;
                element.style.position = "";
                element.style.top = "";
                element.style.width = "";
                element.style.height = "";
                element.style.zIndex = "";
            });
            stickyOffsetsRef.current = [];
            stickyWidthsRef.current = [];
        };
    }, [wrapperId, buttonIdsKey, offset, top]);
};

export default useSticky;
